import VueRouter, { Location, Route } from 'vue-router';

import { CardType } from '~/components/settings/Timeline';

// 併走期間の暫定措置。問診の入力/回答結果だけをWellnessCloud従業員FE(別オリジン)で開く。
// URLとクエリの形はWellnessCloud側READMEが正。

// 既存仕様: confirm.patient.vueが提出時に読むキー
export const QUESTIONNAIRE_CARD_TYPE_STORAGE_KEY = 'questionnaire_card_type';

export const SURVEY_CARD_TYPE_QUERY_KEY = 'cardType';
export const SURVEY_RETURN_TO_QUERY_KEY = 'returnTo';

const WELLNESSCLOUD_CARD_TYPES: string[] = [
  CardType.RegularInterview,
  CardType.Questionnaire,
  CardType.WelcomeQuestionnaire,
];

export type WellnessCloudSurveyScreen = 'input' | 'result';

export const getWellnessCloudBaseUrl = (): string => {
  return (process.env.WELLNESSCLOUD_BASE_URL || '').replace(/\/+$/, '');
};

export const isWellnessCloudEnabled = (): boolean => {
  return getWellnessCloudBaseUrl() !== '';
};

export interface WellnessCloudSurveyUrlParams {
  screen: WellnessCloudSurveyScreen;
  projectId: string;
  questionnaireId: string;
  /** 渡さないとWellnessCloud側の既定(/service)へ戻ってしまう */
  returnTo?: string | null;
  /** WellnessCloudが知らない値は付けない(タイトルから判定させる) */
  cardType?: string | null;
}

export const toWellnessCloudSurveyUrl = (params: WellnessCloudSurveyUrlParams): string => {
  const base = getWellnessCloudBaseUrl();
  const segment = params.screen === 'result' ? 'questionnaire-result' : 'questionnaire';
  const path = `/projects/${encodeURIComponent(params.projectId)}/${segment}/${encodeURIComponent(params.questionnaireId)}`;

  const query = new URLSearchParams();
  if (params.returnTo) {
    query.set('returnTo', params.returnTo);
  }
  if (params.cardType && WELLNESSCLOUD_CARD_TYPES.includes(params.cardType)) {
    query.set('cardType', params.cardType);
  }
  const queryString = query.toString();

  return `${base}${path}${queryString ? `?${queryString}` : ''}`;
};

/**
 * WellnessCloudも別オリジンでAuth0のトークン(refresh token込み)をlocalStorageに持つため、
 * 本アプリのログアウトだけではキャッシュが残り、問診画面のURLを直接開けてしまう。
 * このURLはAuth0のAllowed Logout URLsへの登録が必要。
 */
export const toWellnessCloudLogoutUrl = (returnTo: string): string | undefined => {
  if (!isWellnessCloudEnabled()) {
    return undefined;
  }
  return `${getWellnessCloudBaseUrl()}/logout?returnTo=${encodeURIComponent(returnTo)}`;
};

export const getWellnessCloudOrigin = (): string => {
  return new URL(getWellnessCloudBaseUrl()).origin;
};

// WellnessCloud側はローカルのトークンキャッシュを捨ててから認証し、returnTo(本アプリのパス)へ戻す。
// 前のユーザーのトークンが残っていても入れ替えるため、ルートではなくこのパスを使う。
export const toWellnessCloudLoginUrl = (returnTo: string): string => {
  return `${getWellnessCloudBaseUrl()}/login?returnTo=${encodeURIComponent(returnTo)}`;
};

export const toWellnessCloudRefreshUrl = (): string => {
  return `${getWellnessCloudBaseUrl()}/auth/refresh`;
};

export interface SurveyLinkParams {
  projectId: string;
  questionnaireId: string;
  cardType: string;
  returnTo?: string | null;
}

export interface SurveyResultLinkParams {
  projectId: string;
  questionnaireId: string;
  returnTo?: string | null;
}

/**
 * 同一オリジンの相対パスを返してフルリロードさせる。委譲先が別オリジンでSPA遷移が届かず、
 * 同一オリジンのパス振り分けへ移行したときも同じhrefがそのままReact側に渡るため。
 * cardType / returnToをクエリに載せるのはURL単体で遷移先が決まるようにするため。
 */
export const toSurveyHref = (router: VueRouter, params: SurveyLinkParams): string => {
  const query: Record<string, string> = {
    [SURVEY_CARD_TYPE_QUERY_KEY]: params.cardType,
  };
  if (params.returnTo) {
    query[SURVEY_RETURN_TO_QUERY_KEY] = params.returnTo;
  }
  const location: Location = {
    name: 'questionnaire-custom',
    params: {
      projectId: params.projectId,
      questionnaireId: params.questionnaireId,
    },
    query,
  };
  return router.resolve(location).href;
};

// toSurveyHrefと同じ理由でフルリロード。結果画面は提出しないのでcardTypeは要らない。
export const toSurveyResultHref = (router: VueRouter, params: SurveyResultLinkParams): string => {
  const query: Record<string, string> = {};
  if (params.returnTo) {
    query[SURVEY_RETURN_TO_QUERY_KEY] = params.returnTo;
  }
  const location: Location = {
    name: 'questionnaire-result-custom',
    params: {
      projectId: params.projectId,
      questionnaireId: params.questionnaireId,
    },
    query,
  };
  return router.resolve(location).href;
};

export const toWellnessCloudScreen = (route: Pick<Route, 'name'>): WellnessCloudSurveyScreen | undefined => {
  switch (route.name) {
    case 'questionnaire-custom':
      return 'input';
    case 'questionnaire-result-custom':
      return 'result';
    default:
      return undefined;
  }
};

/**
 * ログイン後の復帰先に委譲対象のパスを使うと、復帰した瞬間に別オリジンへフルページ遷移する。
 * 委譲先が未起動・別アプリだとログインのたびにそこへ飛び、本アプリへ戻れなくなる。
 */
export const isDelegatedSurveyPath = (router: VueRouter, path: string): boolean => {
  if (!isWellnessCloudEnabled()) {
    return false;
  }
  return !!toWellnessCloudScreen(router.resolve(path).route);
};

/**
 * 本アプリの問診画面の「戻る」($s.referrerBack)と規則を揃える。
 * WellnessCloud側はreturnToが無いとサービス画面(/service/integration)へ戻すため必ず渡す。
 */
export const toReturnToPath = (
  from: Pick<Route, 'fullPath' | 'name'>,
  to?: Pick<Route, 'query'>,
): string => {
  // カードの<a href>(フルリロード)で来た場合はfromが初期位置になるため、クエリのreturnToを優先する。
  const fromQuery = to?.query?.[SURVEY_RETURN_TO_QUERY_KEY];
  if (typeof fromQuery === 'string' && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) {
    return fromQuery;
  }
  if (!from || !from.name) {
    return '/';
  }
  return from.fullPath;
};

// クエリ(カードの<a href>)を優先。localStorageは既存仕様のフォールバック。
export const toDelegatedCardType = (to: Pick<Route, 'query'>): string | null => {
  const fromQuery = to.query?.[SURVEY_CARD_TYPE_QUERY_KEY];
  if (typeof fromQuery === 'string' && fromQuery) {
    return fromQuery;
  }
  return localStorage.getItem(QUESTIONNAIRE_CARD_TYPE_STORAGE_KEY);
};
